"use client";

import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import type { SearchItemSchema, SearchResultSchema } from "@packages/sdk";
import { useCallback, useEffect, useRef } from "react";

/**
 * Web half of the cross-platform search event contract. Mirrors Android's
 * `SearchViewModel` / iOS's `DiscoverViewModel` so the search-service A/B
 * dashboards can read all three clients through the same events:
 *
 * - every event carries `search_session_id` (one per screen visit) and
 *   `search_backend` (the arm: search_service | app_search);
 * - `Search Results Shown` is the ordered impression list, deduped by the
 *   response `request_id` — the same id the service logs server-side, so
 *   client impressions join to the server's ranked list;
 * - `Tapped Search Result` carries the typed query (not the result title),
 *   the tapped id, its 0-indexed position and the `request_id` it came from;
 * - `Search Exited` fires once on unmount with per-visit counts, so
 *   abandonment is `taps_count = 0`.
 *
 * Web has a single results section (no Bible tab), so `position_overall`
 * equals `position_of_result` and `section` is always "results".
 */

export type SearchBackend = "search_service" | "app_search";

/** Matches Android's `selectedCategoryName` values. */
export type SearchCategory = "top" | "collection" | "sessions" | "categories";

/** Ordered ids cap on the impression event (Android `MAX_IMPRESSION_IDS`). */
const MAX_IMPRESSION_IDS = 20;
/** Dedupe-set cap before it is reset (Android `MAX_LOGGED_IMPRESSION_IDS`). */
const MAX_LOGGED_IMPRESSION_IDS = 100;
const SCREEN_NAME = "search";

const newSessionId = () => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto)
    return crypto.randomUUID();
  // Non-secure-context fallback; only the per-visit uniqueness matters.
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
};

const resultIdPrefix = (id: string) => id.split("-")[0];

export type UseSearchAnalyticsProps = {
  searchBackend: SearchBackend;
};

export type TrackResultsShownProps = {
  category: SearchCategory;
  languageFilter: string;
  /** All pages fetched so far for the current query, in order. */
  pages: SearchResultSchema[];
  query: string;
};

export type TrackTapProps = {
  category: SearchCategory;
  /** 0-indexed position within the whole rendered results list. */
  position: number;
  query: string;
  requestId: string;
  result: SearchItemSchema;
  totalResults: number;
};

export const useSearchAnalytics = ({
  searchBackend,
}: UseSearchAnalyticsProps) => {
  const { track } = useAnalytics();
  // Ref so the unmount-only effect below always sees the live tracker.
  const trackRef = useRef(track);
  trackRef.current = track;

  const sessionIdRef = useRef<string | null>(null);
  if (sessionIdRef.current === null) sessionIdRef.current = newSessionId();

  const searchBackendRef = useRef(searchBackend);
  searchBackendRef.current = searchBackend;

  const hasStartedRef = useRef(false);
  const queriesCountRef = useRef(0);
  const resultsShownCountRef = useRef(0);
  const tapsCountRef = useRef(0);
  const lastTrackedQueryRef = useRef<string | null>(null);
  const loggedImpressionRequestIdsRef = useRef(new Set<string>());

  const internalTrack = useCallback(
    (event: HallowAnalyticsEvent, properties: Record<string, unknown>) =>
      trackRef.current({
        event,
        properties: {
          screen_name: SCREEN_NAME,
          search_session_id: sessionIdRef.current,
          search_backend: searchBackendRef.current,
          ...properties,
        },
      }),
    [],
  );

  const trackQuery = useCallback(
    (query: string) => {
      if (query.trim().length === 0) return;
      // Re-renders (e.g. kid-account status resolving) must not re-log the
      // same settled query.
      if (query === lastTrackedQueryRef.current) return;
      hasStartedRef.current = true;
      queriesCountRef.current += 1;
      lastTrackedQueryRef.current = query;
      internalTrack(HallowAnalyticsEvent.SearchQuery, { query });
    },
    [internalTrack],
  );

  /**
   * Call whenever the fetched pages for the current query change. Each page
   * is its own request; a page whose `request_id` was already logged is
   * skipped, so re-renders, tab switches and infinite-scroll appends never
   * double-count while every newly fetched page still gets an impression.
   */
  const trackResultsShown = useCallback(
    ({ category, languageFilter, pages, query }: TrackResultsShownProps) => {
      if (query.length === 0 || pages.length === 0) return;
      const logged = loggedImpressionRequestIdsRef.current;

      let positionOffset = 0;
      pages.forEach((page, pageIndex) => {
        const requestId = page.meta.request_id;
        const alreadyLogged = logged.has(requestId);
        if (!alreadyLogged) {
          if (logged.size > MAX_LOGGED_IMPRESSION_IDS) logged.clear();
          logged.add(requestId);

          hasStartedRef.current = true;
          resultsShownCountRef.current += 1;
          const zeroResults = page.results.length === 0;
          internalTrack(HallowAnalyticsEvent.SearchResultsShown, {
            category,
            language_filter: languageFilter,
            page: pageIndex,
            position_offset: positionOffset,
            query,
            request_id: requestId,
            result_ids: page.results
              .slice(0, MAX_IMPRESSION_IDS)
              .map((result) => result.id.raw),
            result_types: page.results
              .slice(0, MAX_IMPRESSION_IDS)
              .map(
                (result) => result.type.raw ?? resultIdPrefix(result.id.raw),
              ),
            results_count: page.results.length,
            total_results: page.meta.page.total_results,
            zero_results: zeroResults,
          });
          if (zeroResults && pageIndex === 0)
            internalTrack(HallowAnalyticsEvent.ViewedNoSearchResult, { query });
        }
        positionOffset += page.results.length;
      });
    },
    [internalTrack],
  );

  const trackTap = useCallback(
    ({
      category,
      position,
      query,
      requestId,
      result,
      totalResults,
    }: TrackTapProps) => {
      hasStartedRef.current = true;
      tapsCountRef.current += 1;
      internalTrack(HallowAnalyticsEvent.TappedSearchResult, {
        category,
        position_of_result: position,
        position_overall: position,
        query,
        request_id: requestId,
        result: result.id.raw,
        result_id_prefix: resultIdPrefix(result.id.raw),
        result_title: result.title.raw,
        result_type: result.type.raw,
        section: "results",
        total_results: totalResults,
      });
    },
    [internalTrack],
  );

  // `Search Exited` = true screen exit (Android `onCleared`), once per visit.
  useEffect(
    () => () => {
      if (!hasStartedRef.current) return;
      internalTrack(HallowAnalyticsEvent.SearchExited, {
        queries_count: queriesCountRef.current,
        query: lastTrackedQueryRef.current,
        results_shown_count: resultsShownCountRef.current,
        taps_count: tapsCountRef.current,
      });
    },
    [internalTrack],
  );

  return { trackQuery, trackResultsShown, trackTap };
};
